import React, {
    FC, useState, SyntheticEvent,
} from 'react';
import { Button, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { PasswordVisibilityToggle, SpInput } from '@/components/Common/Sp';
import appConfig from '@/config/env';
import styles from './OTPAuthPasswordBox.module.scss';

interface OTPAuthPasswordBoxProps {
    onPasswordSubmit: (password: string) => void;
    isLoading?: boolean;
    passwordError?: boolean;
    setPasswordError: (value: boolean) => void;
    wrongPasswordAttempts: number;
    hint?: string | null;
}

const OTPAuthPasswordBox: FC<OTPAuthPasswordBoxProps> = observer(({
    onPasswordSubmit,
    isLoading,
    passwordError = false,
    setPasswordError,
    wrongPasswordAttempts,
    hint,
}) => {
    const { t } = useTranslation();
    const nameSpace = 'recipientPage';
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = () => {
        if (!password.trim() || !wrongPasswordAttempts || isLoading) {
            return;
        }

        onPasswordSubmit(password);
    };

    const handlePasswordChange = (value: string) => {
        setPassword(value);
        if (passwordError) {
            setPasswordError(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && password.trim() && wrongPasswordAttempts > 0) {
            handleSubmit();
        }
    };

    const togglePasswordVisibility = (event: SyntheticEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        setShowPassword((prev) => !prev);
    };

    const useSimplifiedPrompt = appConfig.USE_SIMPLIFIED_PASSWORD_PROMPT;

    return (
        <div className={styles.passwordBoxContainer} data-testid="recipientPage_auth_passwordBoxContainer">
            {useSimplifiedPrompt ? (
                <h2 className={styles.titleSmaller} data-testid="recipientPage_auth_passwordTitle">
                    {t(`${nameSpace}.OTPSteps.simplifiedPasswordPrompt`)}
                </h2>
            ) : (
                <>
                    <h2 className={styles.title} data-testid="recipientPage_auth_passwordTitle">
                        {t(`${nameSpace}.OTPSteps.enterPasswordTitle`)}
                    </h2>
                    {hint && (
                        <div className={styles.hintSection} data-testid="recipientPage_auth_passwordHint">
                            <div className={styles.hintLabel}>
                                {t(`${nameSpace}.OTPSteps.hintLabel`)}
                            </div>
                            <div className={styles.hintText}>
                                {hint}
                                <Tooltip title={t(`${nameSpace}.OTPSteps.hintTooltip`)}>
                                    <InfoCircleOutlined className={styles.infoIcon} />
                                </Tooltip>
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className={styles.inputWrapper} data-testid="recipientPage_auth_passwordInputWrapper">
                <SpInput
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t(`${nameSpace}.OTPSteps.passwordPlaceholder`)}
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                    className={passwordError ? styles.errorInput : ''}
                    disabled={!wrongPasswordAttempts}
                    data-testid="recipientPage_auth_passwordInput"
                />
                <PasswordVisibilityToggle
                    toggleVisibility={togglePasswordVisibility}
                    className={styles.show}
                />
            </div>
            {passwordError && !isLoading && (
                <div className={styles.errorMessage} data-testid="recipientPage_auth_wrongPasswordAttempts">
                    {t(`${nameSpace}.OTPSteps.wrongPassword`, { attemptsRemain: wrongPasswordAttempts })}
                </div>
            )}
            {passwordError && !wrongPasswordAttempts && !isLoading && (
                <div className={styles.errorMessage} data-testid="recipientPage_auth_requestNewPassword">
                    {t(`${nameSpace}.OTPSteps.noAttemptsRemaining`)}
                </div>
            )}
            <Button
                type="primary"
                className={styles.verifyButton}
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!password.trim() || !wrongPasswordAttempts}
                data-testid="recipientPage_auth_verifyPasswordButton"
            >
                {t(`${nameSpace}.OTPSteps.verifyButtonText`)}
            </Button>
        </div>
    );
});

export default OTPAuthPasswordBox;
